import { useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';

import { CreatableSelect } from '@frontend/legacy-components';

import { type FormStateData } from './RulesDetailModal';

type MultiStringInputProps = {
  value: string[];
  onChange: (value: string[]) => void;
  isDisabled?: boolean;
  signalIndex: number;
  groupIndex: number;
  queryIndex: number;
};

type SelectOption = {
  value: string;
  label: string;
};

export const MultiStringInput = ({
  value,
  onChange,
  isDisabled = false,
  signalIndex,
  groupIndex,
  queryIndex,
}: MultiStringInputProps) => {
  const { getFieldProps } = useFormikContext<FormStateData>();

  const fieldProps = getFieldProps(
    `signals.${signalIndex}.groups.${groupIndex}.queries.${queryIndex}.value`,
  );

  // Convert current values to select options format
  const selectedOptions = useMemo(
    () =>
      value.map((item) => ({
        value: item,
        label: item,
      })),
    [value],
  );

  const createOption = useCallback(
    (inputValue: string) => {
      const trimmedValue = inputValue.trim();
      if (trimmedValue && !value.includes(trimmedValue)) {
        onChange([...value, trimmedValue]);
      }
    },
    [value, onChange],
  );

  return (
    <CreatableSelect
      {...fieldProps}
      value={selectedOptions}
      options={selectedOptions}
      isMulti
      isClearable={true}
      isDisabled={isDisabled}
      placeholder="Text in a file"
      className="multi-string-select"
      components={{
        IndicatorsContainer: () => null,
        IndicatorSeparator: () => null,
        Menu: () => null,
      }}
      onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
        // If you type and click away, the value should be added to the list
        createOption(event.target.value);
      }}
      onChange={(newValue: SelectOption[] | null) => {
        if (newValue === null) {
          onChange([]);
        } else {
          const options = Array.isArray(newValue) ? newValue : [newValue];
          onChange(options.map((opt) => opt.value));
        }
      }}
      onCreateOption={createOption}
    />
  );
};
